import { Box, CircularProgress, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { fetchIssuePulse } from 'api'

/**
 * The Issue Tracker's management rollup. Built from mockup variant A (user
 * pick, 2026-08-16): KPI strip, raised-vs-resolved line, the two pile-up bar
 * lists. One GET; the only control is the grain of the line (day / week /
 * month, user 2026-09-15). The board is where filtering lives; this page
 * answers "are we okay this week?".
 *
 * Kept simple on 2026-09-15 (user: "it looks too complicated, keep it simple,
 * remove the table from the bottom, remove the vertical orange line, in the
 * pop up make it blurry white with only raised and resolved"). A Simple /
 * Detailed switch, a per-point breakdown panel and top movers in the hover
 * shipped earlier that day and went with it; the API still sends each
 * point's split, nothing here reads it.
 *
 * The "oldest open issues" list that used to share the row with the chart
 * went on 2026-09-15 (user: "oldest open issue, remove it — instead stretch
 * the graph"); the board's Age sort is where that question is answered now.
 *
 * Shown as **HQ > Issue Tracker > Dashboard** since 2026-08-18. It was called
 * "Pulse" and sat inside the top-level tracker group until then; the file, the
 * component and the `/pulse/alfred` endpoint keep the old name, only the nav
 * label and path moved. Read-only — nothing on this page edits an issue.
 */
const BUCKETS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' }
]
const BUCKET_NOUN = { day: 'days', week: 'weeks', month: 'months' }
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  grid: { display: 'grid', gap: theme.spacing(1.75) },
  kpis: {
    gridTemplateColumns: 'repeat(4, 1fr)',
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr 1fr' }
  },
  one: { marginTop: theme.spacing(1.75) },
  two: {
    gridTemplateColumns: '1fr 1fr',
    marginTop: theme.spacing(1.75),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' }
  },
  card: {
    background: '#fff',
    border: '1px solid #e3ddd3',
    borderRadius: 10,
    padding: theme.spacing(2, 2.25)
  },
  k: {
    fontSize: 11.5,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#8a8177',
    fontWeight: 700
  },
  big: {
    fontSize: 30,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    marginTop: 2
  },
  unit: { fontSize: 16, color: '#8a8177', fontWeight: 500 },
  delta: { fontSize: 12.5, fontWeight: 600 },
  bad: { color: '#d03b3b' },
  good: { color: '#0ca30c' },
  flat: { color: '#8a8177' },
  sub: { fontSize: 12.5, color: '#8a8177', marginTop: 4 },
  cap: { fontSize: 14, fontWeight: 700, marginBottom: theme.spacing(1.25) },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.25)
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap'
  },
  chips: { display: 'flex', gap: theme.spacing(0.75) },
  // Pill styles copied verbatim from the board's status tabs
  // (IssueTracker.js), so the two pages read as one.
  tab: {
    borderRadius: 6,
    padding: theme.spacing(0.5, 1.25),
    fontSize: 13,
    cursor: 'pointer',
    userSelect: 'none',
    border: '1px solid transparent'
  },
  tabOn: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600
  },
  tabOff: { color: '#5f5a52', border: '1px solid #e3ddd3' },
  legend: {
    display: 'flex',
    gap: theme.spacing(2),
    fontSize: 12.5,
    color: '#5f5a52',
    marginTop: theme.spacing(0.75)
  },
  sw: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: 3,
    marginRight: 6,
    verticalAlign: -1
  },
  barRow: {
    display: 'grid',
    gridTemplateColumns: '150px 1fr 40px',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    margin: theme.spacing(0.9, 0)
  },
  barName: {
    fontSize: 13,
    color: '#5f5a52',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  bar: {
    height: 10,
    borderRadius: '0 4px 4px 0',
    background: '#2a78d6',
    minWidth: 2
  },
  barVal: {
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums'
  },
  empty: { padding: theme.spacing(6), textAlign: 'center', color: '#8a8177' }
}))

const Delta = ({ value, goodWhenDown, suffix, classes }) => {
  if (value === null || value === undefined) return null
  if (value === 0) {
    return <span className={`${classes.delta} ${classes.flat}`}>≈ steady</span>
  }
  const up = value > 0
  const isGood = goodWhenDown ? !up : up
  return (
    <span className={`${classes.delta} ${isGood ? classes.good : classes.bad}`}>
      {up ? '▲' : '▼'} {Math.abs(value)}
      {suffix || ''} vs previous
    </span>
  )
}

/** The raised/resolved chart, full card width. Plain SVG: two 2px lines,
 *  endpoint markers with the current values, three recessive gridlines, a
 *  date under every point that has room (every 5th day on the 30-day view).
 *
 *  Hover (user, 2026-09-15: "move the cursor on the line and get the
 *  number"): a dashed hairline snaps to the nearest point — aim at a date,
 *  not at a 2px line — and a frosted white card shows the two numbers
 *  there, nothing else. The card is HTML over the SVG: an SVG shape cannot
 *  blur what is behind it. */
const TrendChart = ({ series, bucket }) => {
  const [hover, setHover] = useState(null)
  const svgRef = useRef(null)
  const W = 1000
  const H = 230
  const left = 44
  const right = 24
  const top = 18
  const floor = 190
  const max = Math.max(4, ...series.map((p) => Math.max(p.raised, p.resolved)))
  const n = series.length
  const x = (i) => left + (i * (W - left - right)) / Math.max(1, n - 1)
  const y = (v) => floor - (v / max) * (floor - top)
  const pts = (key) => series.map((p, i) => `${x(i)},${y(p[key])}`).join(' ')
  const last = series[n - 1] || { raised: 0, resolved: 0 }
  const fmt = (iso) => {
    const d = new Date(`${iso}T00:00:00`)
    return bucket === 'month'
      ? d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
      : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  }
  const step = bucket === 'day' ? 5 : 1
  const labelled = (i) => (n - 1 - i) % step === 0
  const anchor = (i) => (i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle')
  // Pointer x in viewBox units -> nearest point. The SVG scales with the
  // card, so client pixels are converted through its rendered width.
  const nearest = (event) => {
    if (!svgRef.current || !n) return null
    const box = svgRef.current.getBoundingClientRect()
    const px = ((event.clientX - box.left) / box.width) * W
    const i = Math.round(((px - left) / (W - left - right)) * (n - 1))
    return Math.max(0, Math.min(n - 1, i))
  }
  const at = hover !== null ? series[hover] : null
  // The card sits beside the hairline, flipping left near the right edge.
  const along = at ? (x(hover) / W) * 100 : 0
  const tipSide =
    along < 78
      ? { left: `calc(${along}% + 12px)` }
      : { right: `calc(${100 - along}% + 12px)` }
  return (
    <div style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Issues raised versus resolved per ${bucket}`}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          cursor: 'crosshair'
        }}
        onMouseMove={(event) => setHover(nearest(event))}
        onMouseLeave={() => setHover(null)}
      >
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={left}
            x2={W - right}
            y1={y(max * f)}
            y2={y(max * f)}
            stroke={f === 0 ? '#e3ddd3' : '#f0ece4'}
          />
        ))}
        {[0, 0.5, 1].map((f) => (
          <text
            key={f}
            x={left - 8}
            y={y(max * f) + 4}
            textAnchor="end"
            fontSize="11"
            fill="#8a8177"
          >
            {Math.round(max * f)}
          </text>
        ))}
        <polyline
          fill="none"
          stroke="#2a78d6"
          strokeWidth="2"
          points={pts('raised')}
        />
        <polyline
          fill="none"
          stroke="#1baf7a"
          strokeWidth="2"
          points={pts('resolved')}
        />
        {n ? (
          <>
            <circle cx={x(n - 1)} cy={y(last.raised)} r="4" fill="#2a78d6" />
            <circle cx={x(n - 1)} cy={y(last.resolved)} r="4" fill="#1baf7a" />
            <text
              x={x(n - 1) - 8}
              y={y(last.raised) - 8}
              textAnchor="end"
              fontSize="12"
              fontWeight="700"
              fill="#2a78d6"
            >
              {last.raised}
            </text>
            <text
              x={x(n - 1) - 8}
              y={y(last.resolved) + 16}
              textAnchor="end"
              fontSize="12"
              fontWeight="700"
              fill="#1baf7a"
            >
              {last.resolved}
            </text>
          </>
        ) : null}
        {series.map((p, i) =>
          labelled(i) ? (
            <text
              key={p.start}
              x={x(i)}
              y={H - 8}
              fontSize="11"
              fill="#8a8177"
              textAnchor={anchor(i)}
            >
              {fmt(p.start)}
            </text>
          ) : null
        )}
        {at ? (
          <g pointerEvents="none">
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={top}
              y2={floor}
              stroke="#c9c1b5"
              strokeDasharray="3 3"
            />
            <circle
              cx={x(hover)}
              cy={y(at.raised)}
              r="5"
              fill="#2a78d6"
              stroke="#fff"
              strokeWidth="2"
            />
            <circle
              cx={x(hover)}
              cy={y(at.resolved)}
              r="5"
              fill="#1baf7a"
              stroke="#fff"
              strokeWidth="2"
            />
          </g>
        ) : null}
      </svg>
      {at ? (
        <div
          style={{
            position: 'absolute',
            top: `${(top / H) * 100}%`,
            ...tipSide,
            pointerEvents: 'none',
            background: 'rgba(255, 255, 255, 0.72)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(43, 38, 34, 0.08)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(43, 38, 34, 0.10)',
            padding: '6px 10px',
            fontSize: 13,
            lineHeight: '20px',
            color: '#2b2622',
            whiteSpace: 'nowrap'
          }}
        >
          {[
            ['#2a78d6', at.raised, 'raised'],
            ['#1baf7a', at.resolved, 'resolved']
          ].map(([colour, value, label]) => (
            <div key={label}>
              <i
                style={{
                  display: 'inline-block',
                  width: 12,
                  height: 2,
                  background: colour,
                  marginRight: 8,
                  verticalAlign: 4
                }}
              />
              <b>{value}</b> {label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

const Bars = ({ rows, classes }) => {
  const max = Math.max(1, ...rows.map((r) => r.count))
  return rows.map((row) => (
    <div className={classes.barRow} key={row.name}>
      <span className={classes.barName}>{row.name}</span>
      <i
        className={classes.bar}
        style={{ width: `${(row.count / max) * 100}%` }}
      />
      <span className={classes.barVal}>{row.count}</span>
    </div>
  ))
}

const IssuePulse = () => {
  const classes = useStyles()
  const [bucket, setBucket] = useState('week')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  // A grain change refetches the whole rollup (one GET, ~half a second) and
  // swaps it in when it lands; the page keeps showing the previous numbers
  // meanwhile rather than blanking to a spinner.
  const load = useCallback(async (grain) => {
    try {
      setData(await fetchIssuePulse(grain))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(bucket)
  }, [load, bucket])

  if (loading && !data) {
    return (
      <div className={classes.root}>
        <div className={classes.empty}>
          <CircularProgress />
        </div>
      </div>
    )
  }
  if (!data) {
    return (
      <div className={classes.root}>
        <div className={classes.empty}>could not load — try a refresh.</div>
      </div>
    )
  }

  const medianDelta =
    data.median_resolve_days !== null && data.median_resolve_days_prev !== null
      ? Math.round(
          (data.median_resolve_days - data.median_resolve_days_prev) * 10
        ) / 10
      : null
  // Under a day reads as hours, under an hour as "< 1 hour": "0 days" was
  // never a truthful median.
  const med = data.median_resolve_days
  const medHours = med === null ? 0 : Math.round(med * 24)
  const medValue =
    med === null
      ? '—'
      : med >= 1
      ? Math.round(med * 10) / 10
      : medHours >= 1
      ? medHours
      : '< 1'
  const medUnit =
    med === null
      ? ''
      : med >= 1
      ? ' days'
      : medHours === 1 || medHours < 1
      ? ' hour'
      : ' hours'
  const series = data.series || []
  const grain = data.bucket || 'week'

  return (
    <div className={classes.root}>
      <Box className={`${classes.grid} ${classes.kpis}`}>
        <div className={classes.card}>
          <div className={classes.k}>Open now</div>
          <div className={classes.big}>{data.open_now}</div>
          <Delta
            value={data.open_now - data.open_week_ago}
            goodWhenDown
            classes={classes}
          />
        </div>
        <div className={classes.card}>
          <div className={classes.k}>Raised · 7 days</div>
          <div className={classes.big}>{data.raised_7d}</div>
          <Delta
            value={data.raised_7d - data.raised_prev_7d}
            goodWhenDown
            classes={classes}
          />
        </div>
        <div className={classes.card}>
          <div className={classes.k}>Resolved · 7 days</div>
          <div className={classes.big}>{data.resolved_7d}</div>
          <Delta
            value={data.resolved_7d - data.resolved_prev_7d}
            classes={classes}
          />
        </div>
        <div className={classes.card}>
          <div className={classes.k}>Median time to resolve</div>
          <div className={classes.big}>
            {medValue}
            <span className={classes.unit}>{medUnit}</span>
          </div>
          <Delta
            value={medianDelta}
            goodWhenDown
            suffix=" d"
            classes={classes}
          />
          {/* Says what the number is made of. Sheet imports with no resolution
              date are left out rather than counted as same-day, so the
              sample can be small — the reader should see that. */}
          <div className={classes.sub}>
            {data.median_resolve_n
              ? `from ${data.median_resolve_n} resolution${
                  data.median_resolve_n === 1 ? '' : 's'
                } with a known time · last 30 days`
              : 'no resolution with a known time in the last 30 days'}
          </div>
        </div>
      </Box>

      <Box className={classes.one}>
        <div className={classes.card}>
          <div className={classes.cardHead}>
            <Typography className={classes.cap} style={{ marginBottom: 0 }}>
              Raised vs resolved — last {series.length}{' '}
              {BUCKET_NOUN[grain] || 'weeks'}
            </Typography>
            <div className={classes.controls}>
              <div className={classes.chips}>
                {BUCKETS.map((item) => (
                  <div
                    key={item.value}
                    role="button"
                    tabIndex={0}
                    className={`${classes.tab} ${
                      bucket === item.value ? classes.tabOn : classes.tabOff
                    }`}
                    onClick={() => setBucket(item.value)}
                    onKeyPress={() => setBucket(item.value)}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <TrendChart series={series} bucket={grain} />
          <div className={classes.legend}>
            <span>
              <i className={classes.sw} style={{ background: '#2a78d6' }} />
              Raised
            </span>
            <span>
              <i className={classes.sw} style={{ background: '#1baf7a' }} />
              Resolved
            </span>
            {/* The last bucket is still filling, and on the week and month
                views that reads as a cliff. Say so rather than let it. */}
            <span className={classes.sub} style={{ marginTop: 0 }}>
              last point = the current {grain} so far
            </span>
          </div>
        </div>
      </Box>

      <Box className={`${classes.grid} ${classes.two}`}>
        <div className={classes.card}>
          <Typography className={classes.cap}>Open by category</Typography>
          <Bars rows={data.open_by_category || []} classes={classes} />
        </div>
        <div className={classes.card}>
          <Typography className={classes.cap}>Open by centre</Typography>
          <Bars rows={data.open_by_centre || []} classes={classes} />
        </div>
      </Box>
    </div>
  )
}

export default IssuePulse
